import React, { useState, useEffect } from 'react';
import studentService from 'ApiService/Admin/studentService';
import toast from 'react-hot-toast';

const FeeDetails = ({ studentId }) => {
  const [feeData, setFeeData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await studentService.studentPaymentDetails(studentId);
      if (response.data.success) {
        setFeeData(response.data.data || null);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch student details.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const PaymentStatusBadge = ({ status, text }) => {
    const colors = {
      paid: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-600/20',
      pending: 'bg-rose-100 text-rose-800 ring-1 ring-rose-600/20',
      swap: 'bg-violet-100 text-violet-800 ring-1 ring-violet-600/20',
      installments: 'bg-amber-100 text-amber-800 ring-1 ring-amber-600/20',
      full: 'bg-sky-100 text-sky-800 ring-1 ring-sky-600/20',
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors[status]}`}
      >
        {text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl rounded-lg bg-white p-4 shadow-md dark:bg-navy-800">
        <div className="flex items-center justify-center">
          <div className="text-base font-semibold text-gray-700 dark:text-white">
            Loading student details...
          </div>
        </div>
      </div>
    );
  }

  if (!feeData || !feeData.feeBreakdown) {
    return (
      <div className="mx-auto max-w-6xl rounded-lg bg-white p-4 shadow-md dark:bg-navy-800">
        <div className="flex items-center justify-center">
          <div className="text-base font-semibold text-gray-700 dark:text-white">
            No student fee details available
          </div>
        </div>
      </div>
    );
  }

  const { overall, original, swaps } = feeData.feeBreakdown;
  const allFeeDetails = [original, ...(swaps || [])];

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4">
      <div className="relative">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-300 dark:border-gray-800"></div>
        </div>
        <div className="relative flex justify-center">
          <h2 className="bg-white px-3 text-xl font-bold tracking-tight text-gray-900 dark:bg-navy-800 dark:text-white">
            Fee Details - {feeData.student.name}
          </h2>
        </div>
      </div>

      {/* Overall Summary Section */}
      <div className="overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-gray-200 dark:bg-navy-800 dark:ring-navy-600">
        <div className="bg-gray-50 px-4 py-3 dark:bg-navy-700/30">
          <h3 className="mb-3 text-base font-semibold text-gray-800 dark:text-white">
            Overall Summary
          </h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-md bg-white p-3 shadow-sm ring-1 ring-gray-200 dark:bg-navy-800 dark:ring-navy-600">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Total Fees
              </div>
              <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                ₹{overall.totalAmount.toLocaleString()}
              </div>
            </div>
            <div className="rounded-md bg-white p-3 shadow-sm ring-1 ring-gray-200 dark:bg-navy-800 dark:ring-navy-600">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Paid Fees
              </div>
              <div className="text-emerald-600 dark:text-emerald-400 mt-1 text-lg font-semibold">
                ₹{overall.totalPaidAmount.toLocaleString()}
              </div>
            </div>
            <div className="rounded-md bg-white p-3 shadow-sm ring-1 ring-gray-200 dark:bg-navy-800 dark:ring-navy-600">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Remaining Fees
              </div>
              <div className="text-rose-600 dark:text-rose-400 mt-1 text-lg font-semibold">
                ₹{overall.totalPendingAmount.toLocaleString()}
              </div>
            </div>
            <div className="rounded-md bg-white p-3 shadow-sm ring-1 ring-gray-200 dark:bg-navy-800 dark:ring-navy-600">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Payment Status
              </div>
              <div className="mt-1.5">
                {overall.isFullyPaid ? (
                  <PaymentStatusBadge status="paid" text="Fully Paid" />
                ) : (
                  <PaymentStatusBadge status="pending" text="Partially Paid" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Individual Fee Details */}
      {allFeeDetails.map((detail, index) => {
        const isSwap = detail.type === 'swap';
        const title = isSwap ? 'Batch Swap Details' : 'Original Course Details';
        const hasPendingInstallments = detail.installments?.some(
          (i) => !i.isPaid
        );

        return (
          <div
            key={index}
            className="overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-gray-200 dark:bg-navy-800 dark:ring-navy-600"
          >
            {/* Course/Batch Header */}
            <div className="bg-gray-50 px-4 py-3 dark:bg-navy-700/30">
              <h3 className="text-base font-semibold text-gray-800 dark:text-white">
                {title}
              </h3>
              {isSwap ? (
                <div className="mt-2 grid grid-cols-1 gap-y-1 text-sm md:grid-cols-2">
                  <div className="flex items-center gap-x-2">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      From Batch:
                    </span>
                    <span className="text-xs text-gray-900 dark:text-white">
                      Batch {detail.swappedFromBatch.batchNumber} (
                      {detail.swappedFromBatch.timing})
                    </span>
                  </div>
                  <div className="flex items-center gap-x-2">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      To Batch:
                    </span>
                    <span className="text-xs text-gray-900 dark:text-white">
                      Batch {detail.swappedToBatch.batchNumber} (
                      {detail.swappedToBatch.timing})
                    </span>
                  </div>
                  <div className="flex items-center gap-x-2">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Swap Date:
                    </span>
                    <span className="text-xs text-gray-900 dark:text-white">
                      {new Date(detail.swapDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="mt-2 grid grid-cols-1 gap-y-1 text-sm md:grid-cols-2">
                  <div className="flex items-center gap-x-2">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Course:
                    </span>
                    <span className="text-xs text-gray-900 dark:text-white">
                      {detail.course.name} ({detail.course.department})
                    </span>
                  </div>
                  <div className="flex items-center gap-x-2">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Batch:
                    </span>
                    <span className="text-xs text-gray-900 dark:text-white">
                      Batch {detail.batch?.batchNumber || 'N/A'} (
                      {detail.batch?.timing &&
                      !detail.batch.timing.includes('undefined')
                        ? detail.batch.timing
                        : 'Not specified'}
                      )
                    </span>
                  </div>
                  <div className="flex items-center gap-x-2">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Days:
                    </span>
                    <span className="text-xs text-gray-900 dark:text-white">
                      {detail.batch?.days?.length > 0
                        ? detail.batch.days.join(', ')
                        : 'Not specified'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Fee Summary Section */}
            <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-800">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-md bg-white p-3 shadow-sm ring-1 ring-gray-200 dark:bg-navy-800 dark:ring-navy-600">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Total Amount
                  </div>
                  <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                    ₹{detail.totalAmount.toLocaleString()}
                  </div>
                </div>
                <div className="rounded-md bg-white p-3 shadow-sm ring-1 ring-gray-200 dark:bg-navy-800 dark:ring-navy-600">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Paid Amount
                  </div>
                  <div className="text-emerald-600 dark:text-emerald-400 mt-1 text-lg font-semibold">
                    ₹{detail.paidAmount.toLocaleString()}
                  </div>
                </div>
                <div className="rounded-md bg-white p-3 shadow-sm ring-1 ring-gray-200 dark:bg-navy-800 dark:ring-navy-600">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Pending Amount
                  </div>
                  <div className="text-rose-600 dark:text-rose-400 mt-1 text-lg font-semibold">
                    ₹{detail.pendingAmount.toLocaleString()}
                  </div>
                </div>
                <div className="rounded-md bg-white p-3 shadow-sm ring-1 ring-gray-200 dark:bg-navy-800 dark:ring-navy-600">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Payment Type
                  </div>
                  <div className="mt-1.5">
                    {detail.installments && detail.installments.length > 0 ? (
                      <PaymentStatusBadge
                        status="installments"
                        text="Installments"
                      />
                    ) : (
                      <PaymentStatusBadge status="full" text="Full Payment" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Installments Section */}
            {detail.installments?.length > 0 && (
              <div className="border-t border-gray-200 px-4 py-4 dark:border-gray-800">
                <h4 className="mb-4 text-sm font-medium leading-6 text-gray-900 dark:text-white">
                  Installment Details
                </h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {detail.installments.map((installment, idx) => {
                    const isPastDue =
                      !installment.isPaid &&
                      new Date(installment.dueDate) < new Date();

                    return (
                      <div
                        key={idx}
                        className={`relative rounded-md bg-white p-4 shadow-sm ring-1 transition-all hover:shadow dark:bg-navy-700/30 
                          ${
                            isPastDue
                              ? 'ring-rose-300 dark:ring-rose-800/30'
                              : 'ring-gray-200 dark:ring-navy-600'
                          }`}
                      >
                        <div className="absolute right-3 top-3">
                          <PaymentStatusBadge
                            status={installment.isPaid ? 'paid' : 'pending'}
                            text={installment.isPaid ? 'Paid' : 'Pending'}
                          />
                        </div>
                        <div className="mt-3 space-y-2">
                          <div>
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              Amount
                            </div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                              ₹{installment.amount.toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              Due Date
                            </div>
                            <div
                              className={`text-sm ${
                                isPastDue
                                  ? 'text-rose-600 dark:text-rose-400'
                                  : 'text-gray-900 dark:text-white'
                              }`}
                            >
                              {new Date(
                                installment.dueDate
                              ).toLocaleDateString()}
                            </div>
                          </div>
                          {installment.isEmiInstallment && (
                            <div className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20 dark:text-blue-400">
                              EMI
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Payment History Section */}
            {detail.paymentHistory?.length > 0 && (
              <div className="border-t border-gray-200 bg-gray-50 px-4 py-4 dark:border-gray-800 dark:bg-navy-700/30">
                <h4 className="mb-4 text-sm font-medium leading-6 text-gray-900 dark:text-white">
                  Payment History
                </h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {detail.paymentHistory.map((payment, idx) => (
                    <div
                      key={idx}
                      className="rounded-md bg-white p-4 shadow-sm ring-1 ring-gray-200 transition-all hover:shadow dark:bg-navy-800 dark:ring-navy-600"
                    >
                      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                        <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Date
                        </dt>
                        <dd className="text-xs text-gray-900 dark:text-white">
                          {new Date(payment.date).toLocaleDateString()}
                        </dd>
                        <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Amount
                        </dt>
                        <dd className="text-xs font-medium text-gray-900 dark:text-white">
                          ₹{payment.amount.toLocaleString()}
                        </dd>
                        <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Type
                        </dt>
                        <dd className="text-xs text-gray-900 dark:text-white">
                          {payment.paymentType}
                        </dd>
                        {payment.txnNumber && (
                          <>
                            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              Txn
                            </dt>
                            <dd className="font-mono text-xs text-gray-900 dark:text-white">
                              {payment.txnNumber}
                            </dd>
                          </>
                        )}
                      </dl>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FeeDetails;
